import { Clapperboard, Megaphone, type LucideIcon } from 'lucide-react';
import { STREAMS, type StreamDef } from '../studio/StreamPicker';

/**
 * The THREE creator PURPOSES that front the whole product (the narrowed vertical, 2026-08-06). A purpose is
 * the top-level "what are you completing?" choice on the landing + onboarding; each maps to one or more of the
 * built Studio work STREAMS (StreamPicker) and carries the copy for a slick, momentum-first first run.
 *
 * Single source of truth — the landing tiles and the onboarding wizard both consume this, so the two never drift.
 * Presentational data only (labels/icons/copy); no store/service imports (SteveJobs layering).
 */
export type PurposeKey = 'video' | 'photobook' | 'ad';

export interface PurposeDef {
  key: PurposeKey;
  label: string;
  /** One-line landing/onboarding blurb. */
  blurb: string;
  Icon: LucideIcon;
  /** The default Studio stream this purpose opens into. */
  defaultStream: StreamDef;
  /** Momentum copy — the pre-filled first prompt placeholder when the studio opens. */
  firstPrompt: string;
  /** True when this purpose is offered on the public landing page (see LANDING_PURPOSES). */
  landing: boolean;
  /**
   * PUBLIC-FACING copy, used ONLY on the landing page. The public page sells the AI crew and stays
   * deliberately vague about the format and the business model — see LANDING_PURPOSES.
   */
  landingLabel?: string;
  landingBlurb?: string;
}

const stream = (preset: StreamDef['preset']): StreamDef => {
  const s = STREAMS.find((x) => x.preset === preset);
  if (!s) throw new Error(`unknown stream preset: ${preset}`);
  return s;
};

// Only LIVE purposes appear (nothing teased — we never advertise what isn't built). Photobook is added
// here as a new entry when Phase C ships its pipeline.
export const PURPOSES: PurposeDef[] = [
  {
    key: 'video',
    label: 'Micro-drama',
    blurb: 'A serialized vertical drama — idea to finished, bingeable episodes your audience follows.',
    Icon: Clapperboard,
    defaultStream: stream('micro_drama'),
    firstPrompt: 'Describe your micro-drama in one line…',
    /** The one thing we lead with — see LANDING_PURPOSES. */
    landing: true,
    landingLabel: 'Make a film',
    landingBlurb: 'One line of idea in. A finished, original film out — cast, score and all.',
  },
  {
    key: 'ad',
    label: 'Ad spot',
    blurb: 'A scroll-stopping vertical commercial for your product — hooks that convert.',
    Icon: Megaphone,
    defaultStream: stream('product_ad'),
    firstPrompt: 'What product are we selling, and to whom?',
    // Kept as a live purpose INSIDE the product (a stream, an intent target, a Studio tile) but OFF the
    // landing page: ad spots are a capability many tools have, not our unique value proposition. Fronting
    // them alongside micro-drama makes a visitor choose between two things instead of starting the one we
    // are actually better at. (Owner decision 2026-08-12.)
    landing: false,
  },
];

/**
 * What the LANDING offers. Deliberately ONE choice: a visitor's first decision should be to start, not to
 * pick between products. The rest of the purposes stay reachable once they are inside.
 *
 * The landing copy is SWAPPED IN here rather than in the picker, so the public page and the in-product
 * wizard can say different things from one definition. The public page sells the AI crew and the finished
 * film; the FORMAT ("micro-drama", vertical, mobile) and the money story are an in-funnel reveal, not a
 * headline — publishing the wedge invites copycats, and a visitor who has not seen the output yet does not
 * care what the format is called. (Owner decision 2026-08-13.)
 */
export const LANDING_PURPOSES: PurposeDef[] = PURPOSES
  .filter((p) => p.landing)
  .map((p) => ({ ...p, label: p.landingLabel ?? p.label, blurb: p.landingBlurb ?? p.blurb }));

export const purposeByKey = (key: string | null | undefined): PurposeDef | undefined =>
  PURPOSES.find((p) => p.key === key);
